/**
 * `useInfiniteFetch` — the preset's infinite-scroll trigger: fires `fetchNextPage` once
 * when the last loaded row enters the virtual window with more to load and no fetch in
 * flight. Runs in an effect (fetching is a side effect), keyed on the mounted-end + loaded
 * count. A pending load-more error suspends the auto-fetch so the end row can't re-trigger
 * it in a tight loop against the same failure; the user recovers via the inline retry.
 */
import { useEffect } from 'react'
import { useEvent } from 'react-use-event-hook'
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import type { DataTableInfiniteSource } from './data-table.types'

export type UseInfiniteFetchOptions = {
  /** The infinite source (undefined for the client / paged tiers — the hook is inert). */
  infinite: DataTableInfiniteSource | undefined
  /** A pending load-more error suspends auto-fetch until the user retries. */
  loadMoreError: unknown
  isWindowed: boolean
  virtualizer: Virtualizer<HTMLElement, Element>
  /**
   * Loaded server-offset count — the last loaded slot. Anchoring the trigger here (not
   * `serverTotal`) fires fetch-next at the end of loaded data, not the far end of the
   * sparse spacer.
   */
  loadedOffsetCount: number
}

export type InfiniteFetch = {
  /** The current windowed items (undefined when not windowed). */
  virtualItems: VirtualItem[] | undefined
}

export const useInfiniteFetch = (options: UseInfiniteFetchOptions): InfiniteFetch => {
  const { infinite, loadMoreError, isWindowed, virtualizer, loadedOffsetCount } = options

  const virtualItems = isWindowed ? virtualizer.getVirtualItems() : undefined
  const lastMountedIndex = virtualItems?.at(-1)?.index ?? -1

  const maybeFetchNext = useEvent(() => {
    if (infinite === undefined) {
      return
    }
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = infinite
    // Fires when the mounted window reaches the loaded frontier. The scrollbar is sized
    // to loaded + a short placeholder lookahead (`resolveVirtualCount`), so holding the
    // bottom keeps this true as each completed page extends the frontier: reaching a far
    // offset costs one sequential fetch PER intervening page (a `useInfiniteQuery` is
    // contiguous-only — it cannot jump to page N). Bounded and terminating
    // (`isFetchingNextPage` blocks concurrent fires, `loadedOffsetCount` strictly grows),
    // NOT a tight loop. A consumer that needs true random-access deep scroll wires a
    // page-addressed fetch (the deferred sparse fill strategy), not this frontier trigger.
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      loadMoreError == null &&
      lastMountedIndex >= loadedOffsetCount - 1
    ) {
      fetchNextPage()
    }
  })
  useEffect(
    function fetchNextWhenEndMounts() {
      maybeFetchNext()
    },
    [
      maybeFetchNext,
      lastMountedIndex,
      loadedOffsetCount,
      infinite?.isFetchingNextPage,
      loadMoreError,
    ],
  )

  return { virtualItems }
}
